/**
 * Get API base URL based on environment
 */
export const getApiBaseUrl = () => {
    const isDev = import.meta.env.DEV;

    if (isDev) {
        // Development: use proxy configured in vite.config.js
        return '';
    } else {
        // Production: use environment variable or default
        return import.meta.env.VITE_API_BASE_URL || 'https://dinoshort-1.onrender.com';
    }
};

/**
 * Build API URL
 * @param {string} path - API path (e.g., '/api/dramas')
 * @returns {string} Full API URL
 */
export const api = (path) => {
    const base = getApiBaseUrl();
    return base ? `${base}${path}` : path;
};

/**
 * Common fetch wrapper with error handling
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export const apiFetch = async (url, options = {}) => {
    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('API Fetch Error:', error);
        throw error;
    }
};

/**
 * Get user token from localStorage (Zustand persist)
 * @returns {string|null} User token
 */
export const getUserToken = () => {
    try {
        // First check Zustand persist storage
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
            const parsed = JSON.parse(authStorage);
            if (parsed.state && parsed.state.token) {
                return parsed.state.token;
            }
        }
        
        // Fallback: check old userToken key (for backward compatibility)
        const oldToken = localStorage.getItem('userToken');
        if (oldToken) {
            return oldToken;
        }
        
        return null;
    } catch (error) {
        console.error('[getUserToken] Error:', error);
        return null;
    }
};

/**
 * Get user data from localStorage (Zustand persist)
 * @returns {object|null} User data
 */
export const getUserData = () => {
    try {
        // First check Zustand persist storage
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
            const parsed = JSON.parse(authStorage);
            if (parsed.state && parsed.state.user) {
                return parsed.state.user;
            }
        }
        
        // Fallback: check old userData key (for backward compatibility)
        const oldUserData = localStorage.getItem('userData');
        if (oldUserData) {
            return JSON.parse(oldUserData);
        }
        
        return null;
    } catch (error) {
        console.error('[getUserData] Error:', error);
        return null;
    }
};

/**
 * Check if user is logged in
 * @returns {boolean}
 */
export const isLoggedIn = () => {
    const token = getUserToken();
    const userData = getUserData();
    return !!(token && userData && userData.email);
};

/**
 * Get admin token from localStorage
 * @returns {string|null} Admin token
 */
export const getAdminToken = () => {
    try {
        const adminToken = localStorage.getItem('adminToken');
        return adminToken || null;
    } catch (error) {
        console.error('[getAdminToken] Error:', error);
        return null;
    }
};
